import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { api, type KbTree, type KbTreeNode } from './api';
import { useCurrentProject } from './project';

/**
 * One project's knowledge-base tree, shared between the pages panel that renders it and the
 * document view that mutates it.
 *
 * Owned above both rather than by either: `KbTreePanel` reads it to draw the tree, and the Kb
 * page reads `allNodes` for its move picker and calls `reload()` after an edit so the rows beside
 * it settle. For a while the tree was nested into the app sidebar under every project row; it is
 * back to being a view inside the project, because the sidebar is how people move between
 * clients and projects and a page tree is not part of that.
 *
 * Mounted per KB route rather than app-wide: pages outside the knowledge base have no use for it,
 * nor for the request it costs.
 */
export interface KbTreeValue {
  projectId: string | null;
  projectSlug: string;
  tree: KbTree | null;
  /** Depth-first flattening — what the move and create pickers list. */
  allNodes: KbTreeNode[];
  error: string | null;
  reload: () => void;
}

const KbTreeContext = createContext<KbTreeValue | null>(null);

export function flattenPaths(nodes: KbTreeNode[], out: KbTreeNode[] = []): KbTreeNode[] {
  for (const node of nodes) {
    out.push(node);
    flattenPaths(node.children, out);
  }
  return out;
}

export function KbTreeProvider({ children }: { children: ReactNode }) {
  const { project, projectSlug } = useCurrentProject();
  const projectId = project?.id ?? null;

  const [tree, setTree] = useState<KbTree | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!projectId) return;
    api.kb.tree(projectId).then(
      (next) => { setTree(next); setError(null); },
      (e) => setError(e.message),
    );
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  const allNodes = useMemo(() => (tree ? flattenPaths(tree.nodes) : []), [tree]);

  const value = useMemo<KbTreeValue>(
    () => ({ projectId, projectSlug, tree, allNodes, error, reload }),
    [projectId, projectSlug, tree, allNodes, error, reload],
  );

  return <KbTreeContext.Provider value={value}>{children}</KbTreeContext.Provider>;
}

/** For the KB view and its pages panel, which are always inside the provider. */
export function useKbTree(): KbTreeValue {
  const value = useContext(KbTreeContext);
  if (!value) throw new Error('useKbTree must be used inside a <KbTreeProvider>');
  return value;
}
